import { useEffect, useMemo, useRef, useState } from "react";
import s from "./OceanOperator.module.scss";

const suggestions = ["今日のシュノーケル", "ビーチルのおすすめは？", "今日の星空", "サンセットは？", "今日のランチ", "おすすめのカフェ", "今日の夜ご飯", "今から夜遊び", "締めご飯は？", "おすすめホテル"];

const getNumber = (value) => {
  const parsed = Number.parseFloat(String(value ?? "").replace(/[^\d.-]/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const joinTimes = (items = []) => items.slice(0, 2).map((item) => item.time || item).filter(Boolean).join("、");

const japaneseWeekdays = ["日", "月", "火", "水", "木", "金", "土"];

const getJstNow = () => new Date(new Date().toLocaleString("en-US", { timeZone: "Asia/Tokyo" }));

const isClosedToday = (place, now = getJstNow()) => {
  const closed = String(place?.closed || "").replace(/\s/g, "");
  if (!closed || /なし|無休|年中無休|要確認|不定休/.test(closed)) return false;
  const day = japaneseWeekdays[now.getDay()];
  return new RegExp(`${day}(?:曜(?:日)?)?`).test(closed);
};

const getOpeningRanges = (hours) => {
  const normalized = String(hours || "")
    .replace(/[０-９]/g, (character) => String.fromCharCode(character.charCodeAt(0) - 0xfee0))
    .replace(/：/g, ":");
  return [...normalized.matchAll(/(\d{1,2}):?(\d{2})?\s*[〜～~-]\s*(\d{1,2}):?(\d{2})?/g)]
    .map((match) => ({
      start: Number(match[1]) * 60 + Number(match[2] || 0),
      end: Number(match[3]) * 60 + Number(match[4] || 0),
    }));
};

const isOpenNow = (place, now = getJstNow()) => {
  if (isClosedToday(place, now)) return false;
  const ranges = getOpeningRanges(place?.hours);
  if (!ranges.length) return null;
  const minutes = now.getHours() * 60 + now.getMinutes();
  return ranges.some(({ start, end }) => (
    end <= start ? minutes >= start || minutes < end : minutes >= start && minutes < end
  ));
};

const rotateDaily = (items, now = getJstNow()) => {
  if (items.length < 2) return items;
  const start = (now.getMonth() * 31 + now.getDate()) % items.length;
  return [...items.slice(start), ...items.slice(0, start)];
};

const placeSummary = (place, { hotel = false } = {}) => {
  const details = [place.genre, place.area];
  if (hotel) {
    details.push(place.priceRange, place.oceanView, place.airportAccess);
  } else {
    if (place.hours) details.push(`営業時間は${place.hours}`);
    if (place.closed) details.push(`定休日は${place.closed}`);
  }
  return `${place.name}（${details.filter(Boolean).join("、")}）`;
};

const OceanOperator = ({
  oceanCondition,
  weatherForecast,
  dailyMarineForecast,
  hourlyWindForecast,
  typhoonInfo,
  spots = [],
  places = [],
  recommendations = { snorkel: [], beach: [] },
  skyInsights = { stars: {}, sunset: {} },
  tomorrowPlan = { available: false },
  setScreen,
}) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [messages, setMessages] = useState([
    { role: "operator", text: "宮古島オペレーターです。海況、星空、サンセット、ランチ、カフェ、夜ご飯、夜遊び、締めご飯、ホテルまでご案内します。" },
  ]);
  const recognitionRef = useRef(null);
  const askRef = useRef(null);
  const pendingTranscriptRef = useRef("");
  const recognitionSubmittedRef = useRef(false);
  const messageListRef = useRef(null);

  const currentWeather = weatherForecast?.current || {};
  const currentWind = useMemo(() => {
    const items = hourlyWindForecast?.items || [];
    const currentHour = new Date().getHours();
    return [...items].reverse().find((item) => Number.parseInt(item.time, 10) <= currentHour) || items[0] || {};
  }, [hourlyWindForecast]);

  useEffect(() => {
    messageListRef.current?.scrollTo({ top: messageListRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    const Recognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!Recognition) return undefined;
    const recognition = new Recognition();
    recognition.lang = "ja-JP";
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;
    recognition.onstart = () => setListening(true);
    recognition.onend = () => {
      setListening(false);
      const transcript = pendingTranscriptRef.current.trim();
      if (transcript && !recognitionSubmittedRef.current) {
        recognitionSubmittedRef.current = true;
        pendingTranscriptRef.current = "";
        askRef.current?.(transcript);
      }
    };
    recognition.onerror = (event) => {
      setListening(false);
      if (event.error === "aborted") return;
      const errorMessages = {
        "not-allowed": "マイクの使用が許可されていません。ブラウザの設定で、このサイトのマイクを許可してください。",
        "service-not-allowed": "音声認識を利用できません。ブラウザの音声認識とマイクの許可を確認してください。",
        "audio-capture": "マイクを利用できません。端末のマイク設定を確認してください。",
        "no-speech": "音声を聞き取れませんでした。マイクに近づいて、もう一度お試しください。",
        network: "音声認識サービスに接続できませんでした。通信状態を確認して、もう一度お試しください。",
      };
      setMessages((items) => [...items, {
        role: "operator",
        text: errorMessages[event.error] || "音声を認識できませんでした。もう一度お試しください。",
      }]);
    };
    recognition.onresult = (event) => {
      let transcript = "";
      let final = false;
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        transcript += event.results[index][0].transcript;
        final ||= event.results[index].isFinal;
      }
      const normalizedTranscript = transcript.trim();
      pendingTranscriptRef.current = normalizedTranscript;
      setInput(normalizedTranscript);
      if (final && normalizedTranscript && !recognitionSubmittedRef.current) {
        recognitionSubmittedRef.current = true;
        pendingTranscriptRef.current = "";
        askRef.current?.(normalizedTranscript);
      }
    };
    recognitionRef.current = recognition;
    return () => recognition.abort();
  }, []);

  const speak = (text) => {
    if (!voiceEnabled || !("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = "ja-JP";
    utterance.rate = 0.9;
    utterance.pitch = 0.72;
    const voices = window.speechSynthesis.getVoices();
    utterance.voice = voices.find((voice) => /Otoya|Ichiro|Keita|Japanese Male/i.test(voice.name))
      || voices.find((voice) => voice.lang === "ja-JP")
      || null;
    utterance.onstart = () => setSpeaking(true);
    utterance.onend = () => setSpeaking(false);
    utterance.onerror = () => setSpeaking(false);
    window.speechSynthesis.speak(utterance);
  };

  const stopSpeaking = () => {
    window.speechSynthesis?.cancel();
    setSpeaking(false);
  };

  const closePanel = () => {
    pendingTranscriptRef.current = "";
    recognitionSubmittedRef.current = true;
    recognitionRef.current?.stop();
    stopSpeaking();
    setOpen(false);
  };

  const buildAnswer = (question) => {
    const q = question.replace(/[\s。、！？?]/g, "").toLowerCase();
    const weather = currentWeather.weather || oceanCondition?.weather || "取得中";
    const high = currentWeather.high || "--";
    const low = currentWeather.low || "--";
    const wave = dailyMarineForecast?.wave || "--";
    const swell = dailyMarineForecast?.swell || "--";
    const windDirection = currentWind.direction || oceanCondition?.windDirection || "--";
    const windSpeed = currentWind.speed || oceanCondition?.windSpeed || "--";
    const gust = currentWind.gust || oceanCondition?.windGust;
    const matchedSpot = spots.find((spot) =>
      q.includes(String(spot.name || "").replace(/[\s・]/g, "").toLowerCase())
      || (spot.reading && q.includes(String(spot.reading).replace(/\s/g, ""))));
    const matchedPlace = places.find((place) =>
      q.includes(String(place.name || "").replace(/[\s・]/g, "").toLowerCase()));

    if (matchedPlace) {
      const closedToday = isClosedToday(matchedPlace);
      const openNow = isOpenNow(matchedPlace);
      const businessStatus = closedToday
        ? "登録情報では今日は定休日です。"
        : openNow === true
          ? "登録情報では現在営業時間内です。"
          : openNow === false
            ? "登録情報では現在は営業時間外です。"
            : "営業状況はお店へご確認ください。";
      const contact = [
        matchedPlace.hours && `営業時間は${matchedPlace.hours}。`,
        matchedPlace.closed && `定休日は${matchedPlace.closed}。`,
        matchedPlace.phone && `電話番号は${matchedPlace.phone}。`,
        (matchedPlace.address || matchedPlace.area) && `場所は${matchedPlace.address || matchedPlace.area}です。`,
      ].filter(Boolean).join("");
      return `${matchedPlace.name}は、${matchedPlace.detail || matchedPlace.genre || "Ocean Guide掲載スポットです。"}${businessStatus}${contact}`;
    }

    if (matchedSpot) {
      const condition = matchedSpot.operatorCondition?.status
        ? `ページ上の本日の評価は「${matchedSpot.operatorCondition.status}」です。`
        : "";
      if (/駐車|トイレ|シャワー|設備/.test(q)) {
        return `${matchedSpot.name}の情報です。${matchedSpot.parking || "駐車場情報はページに登録されていません。"}${matchedSpot.facilities ? ` ${matchedSpot.facilities}。` : ""}${condition}`;
      }
      return `${matchedSpot.name}は、${matchedSpot.description || "Ocean Guide掲載スポットです。"}${condition}`;
    }

    if (/明日/.test(q) && /プラン|予定|過ごし方|コース/.test(q)) {
      if (!tomorrowPlan.available) {
        return "明日の詳しい天気・風・海況データをまだ取得できていません。予報が更新された後に、もう一度聞いてください。";
      }

      const tomorrowWeather = tomorrowPlan.weather || {};
      const tomorrowMarine = tomorrowPlan.marine || {};
      const tomorrowRecommendations = tomorrowPlan.recommendations || { snorkel: [], beach: [] };
      const snorkelNames = tomorrowRecommendations.snorkel.map((spot) => spot.name);
      const beachNames = tomorrowRecommendations.beach.map((spot) => spot.name);
      const rain = tomorrowPlan.rainProbability || 0;
      const seaType = tomorrowPlan.seaRating?.type;
      const seaIsRisky = seaType === "bad" || seaType === "caution";
      const rainyPlan = rain >= 50 || /雨/.test(String(tomorrowWeather.weather || ""));
      const forecast = `明日は${tomorrowWeather.weather || "天気確認中"}、最高${tomorrowWeather.high || "--"}度、最低${tomorrowWeather.low || "--"}度、日中の降水確率は${rain}パーセントです。波は${tomorrowMarine.wave || "--"}、うねりは${tomorrowMarine.swell || "--"}、風は${tomorrowPlan.windDirection || "--"}で最大${tomorrowPlan.maxWindSpeed || "--"}メートルの予報です。`;

      if (rainyPlan || seaIsRisky || (!snorkelNames.length && !beachNames.length)) {
        return `${forecast}明日は海を主目的にせず、午前は雨の日画面にある室内体験やカフェ、午後は雨雲と風を確認しながら島内ドライブや海を眺めるプランがおすすめです。海へ入る場合は当日の更新と現地の遊泳情報を確認し、安全が確認できた時間帯だけにしてください。`;
      }

      const morning = snorkelNames.length
        ? `午前は風が強まる前に${snorkelNames[0]}でシュノーケル`
        : `午前は${beachNames[0]}でビーチ散策`;
      const afternoonSpot = beachNames.find((name) => name !== snorkelNames[0]) || snorkelNames[1];
      const afternoon = afternoonSpot
        ? `午後は${afternoonSpot}でゆっくり過ごす`
        : "午後は海況を再確認して島内観光に切り替える";
      return `${forecast}${morning}、ランチ休憩を挟んで、${afternoon}流れがおすすめです。夕方は予報を再確認し、天気が保てば日没30分前からサンセットへ。出発前にこのページの明日評価をもう一度確認してください。`;
    }

    const wantsNow = /今から|今すぐ|現在|開いてる|営業中/.test(q);
    const requestedPlaceCategory = /締め|〆|シメ|深夜ご飯|深夜飯/.test(q)
      ? "lateNightMeal"
      : /夜遊び|ナイトスポット|飲みに|おすすめ(?:の)?バー|バー(?:に|へ|は|おすすめ)/.test(q)
        ? "nightlife"
        : /ディナー|夜ご飯|晩ご飯|夕食/.test(q)
          ? "dinner"
          : /ランチ|昼ご飯|昼食/.test(q)
            ? "lunch"
            : /カフェ|喫茶|スイーツ/.test(q)
              ? "cafe"
              : /ホテル|宿|宿泊/.test(q)
                ? "hotel"
                : null;

    if (requestedPlaceCategory) {
      const now = getJstNow();
      const categoryPlaces = places.filter((place) =>
        place.categories?.includes(requestedPlaceCategory));
      const openToday = categoryPlaces.filter((place) =>
        requestedPlaceCategory === "hotel" || !isClosedToday(place, now));
      const openNow = openToday.filter((place) => isOpenNow(place, now) === true);
      const candidates = wantsNow && requestedPlaceCategory !== "hotel" ? openNow : openToday;
      const fallbackCandidates = candidates.length ? candidates : openToday;
      const selected = rotateDaily(fallbackCandidates, now)
        .sort((a, b) => Number(isOpenNow(b, now) === true) - Number(isOpenNow(a, now) === true))
        .slice(0, 3);
      const categoryNames = {
        lunch: "ランチ",
        cafe: "カフェ",
        dinner: "夜ご飯",
        nightlife: "夜遊び",
        lateNightMeal: "締めご飯",
        hotel: "ホテル",
      };
      const categoryName = categoryNames[requestedPlaceCategory];

      if (!selected.length) {
        return wantsNow
          ? `登録情報上、今から営業中と確認できる${categoryName}の候補がありません。営業時間が変わる場合があるため、マップから各店舗へ直接ご確認ください。`
          : `登録情報上、今日営業している${categoryName}の候補を確認できませんでした。マップで最新の店舗情報をご確認ください。`;
      }

      const summaries = selected.map((place) =>
        placeSummary(place, { hotel: requestedPlaceCategory === "hotel" }));
      const timing = wantsNow && requestedPlaceCategory !== "hotel"
        ? "登録された営業時間では、現在営業中の候補です。"
        : requestedPlaceCategory === "hotel"
          ? "エリアや予算、オーシャンビューの条件で選んでください。"
          : "今日の曜日と定休日を確認した候補です。営業時間は変更される場合があります。";
      return `${categoryName}のおすすめは、${summaries.join("、")}です。${timing}予約や来店前に、電話または公式Instagramで最新情報をご確認ください。`;
    }

    if (/天気|気温|雨/.test(q)) {
      return `今日の宮古島は${weather}、最高${high}度、最低${low}度の予報です。${currentWeather.summary || "空の変化を確認しながら行動してください。"}`;
    }
    if (/海に入|泳げ|シュノーケル|海況|安全/.test(q)) {
      const waveHeight = getNumber(wave);
      const wind = getNumber(windSpeed);
      const snorkelNames = recommendations.snorkel.map((spot) => spot.name);
      if (/シュノーケル/.test(q) && /おすすめ|どこ|今日|スポット/.test(q) && snorkelNames.length) {
        return `今日のシュノーケルおすすめスポットは、${snorkelNames.join("、")}です。現在の目安は波${wave}、風は${windDirection}${windSpeed}です。Ocean Guideの風・波・スポットの向きを合わせて選んでいますが、入る前に現地の海況も確認してください。`;
      }
      if (waveHeight >= 1.5 || wind >= 8) {
        return `現在の目安は波${wave}、${windDirection}の風${windSpeed}です。海遊びには注意が必要です。無理に入らず、現地の遊泳情報とライフセーバーの案内を優先してください。`;
      }
      return `現在の目安は波${wave}、${windDirection}の風${windSpeed}です。比較的楽しみやすい可能性がありますが、場所ごとの差が大きいため、マップで風を避けられるビーチを確認してください。`;
    }
    if (/波|風|うねり/.test(q)) {
      return `現在は波${wave}、うねり${swell}、風は${windDirection}${windSpeed}${gust ? `、最大瞬間風速は${gust}` : ""}です。数値は目安なので、海へ入る前に現地の様子も確認してください。`;
    }
    if (/潮|満潮|干潮/.test(q)) {
      const highTides = joinTimes(dailyMarineForecast?.tides?.high);
      const lowTides = joinTimes(dailyMarineForecast?.tides?.low);
      return `今日の満潮は${highTides || "取得中"}、干潮は${lowTides || "取得中"}です。潮位が変わる時間帯は流れにも注意してください。`;
    }
    if (/夕日|サンセット|日没/.test(q)) {
      const sunset = skyInsights.sunset || {};
      const spotNames = (sunset.spots || []).map((spot) => spot.name);
      const spotText = spotNames.length
        ? `本日の評価が高いスポットは${spotNames.join("、")}です。`
        : "本日のページ評価では、積極的におすすめできるサンセットスポットがありません。";
      return `今日の日の入りは${sunset.sunsetTime || currentWeather.sunset || "取得中"}、おすすめ時間は${sunset.bestTime || "日没30分前から"}です。天気は${sunset.weather || weather}、総合評価は「${sunset.rating?.status || "取得中"}」です。${spotText}`;
    }
    if (/星|星空|天の川/.test(q)) {
      const stars = skyInsights.stars || {};
      const rating = stars.tonightRating || {};
      const spotNames = (stars.spots || []).map((spot) => spot.name);
      const factors = [
        rating.weather?.text && `夜の天気は${rating.weather.text}`,
        Number.isFinite(rating.nightCloudCover) && `雲量は${rating.nightCloudCover}パーセント`,
        rating.wind?.label && `風は${rating.wind.label}`,
        rating.moon?.label && `月明かりは${rating.moon.label}`,
      ].filter(Boolean).join("、");
      const timeText = stars.bestTime ? `おすすめ時間は${stars.bestTime}です。` : "";
      const spotText = spotNames.length ? `星空スポット候補は${spotNames.join("、")}です。` : "";
      return `今夜の星空の総合評価は「${rating.status || "取得中"}」です。${factors ? `${factors}です。` : ""}${stars.recommendation || ""}${timeText}${spotText}`;
    }
    if (/おすすめ|ビーチ|スポット|ウミガメ/.test(q)) {
      const snorkelNames = recommendations.snorkel.map((spot) => spot.name);
      const beachNames = recommendations.beach.map((spot) => spot.name);
      const parts = [];
      const asksBeachLounge = /ビーチル|ビーチで過ご|海を見ながら|砂浜/.test(q);
      const asksSnorkel = /シュノーケル/.test(q);
      if (!asksBeachLounge && snorkelNames.length) parts.push(`シュノーケルは${snorkelNames.join("、")}`);
      if (!asksSnorkel && beachNames.length) parts.push(`ビーチで過ごすなら${beachNames.join("、")}`);
      if (!parts.length) {
        return `ページの本日の評価では、安全におすすめできるスポットがありません。現在は波${wave}、風は${windDirection}${windSpeed}です。海遊びを控える判断も検討してください。`;
      }
      return `このページの本日の海況評価では、${parts.join("。")}がおすすめです。現在の波${wave}、${windDirection}の風${windSpeed}と、各スポットの風・波の受け方を反映しています。`;
    }
    if (/台風/.test(q)) {
      const activeTyphoons = typhoonInfo?.items || [];
      return activeTyphoons.length
        ? `台風情報が${activeTyphoons.length}件あります。進路や交通への影響は最新の気象情報も合わせて確認してください。`
        : "現在、このアプリで表示中の台風情報はありません。出発前には気象庁の最新情報も確認してください。";
    }
    if (/何ができ|使い方|教えて/.test(q)) {
      return "シュノーケル、ビーチル、星空、サンセット、ランチ、カフェ、夜ご飯、夜遊び、締めご飯、ホテルについて質問できます。飲食店は営業時間、今日の曜日、定休日も確認して回答します。";
    }
    return "その質問はまだ学習中です。シュノーケル、ビーチル、星空、サンセット、ランチ、カフェ、夜ご飯、夜遊び、締めご飯、ホテルについて聞いてください。";
  };

  const ask = (rawQuestion) => {
    const question = rawQuestion.trim();
    if (!question) return;
    const answer = buildAnswer(question);
    setMessages((items) => [...items, { role: "user", text: question }, { role: "operator", text: answer }]);
    setInput("");
    speak(answer);
  };
  askRef.current = ask;

  const toggleListening = () => {
    const recognition = recognitionRef.current;
    if (!recognition) {
      setMessages((items) => [...items, { role: "operator", text: "このブラウザは音声認識に対応していません。ChromeまたはEdgeでお試しください。" }]);
      return;
    }
    if (listening) recognition.stop();
    else {
      setOpen(true);
      pendingTranscriptRef.current = "";
      recognitionSubmittedRef.current = false;
      setInput("");
      try {
        recognition.start();
      } catch {
        setListening(false);
        setMessages((items) => [...items, {
          role: "operator",
          text: "音声入力を開始できませんでした。数秒待ってから、もう一度マイクボタンを押してください。",
        }]);
      }
    }
  };

  return (
    <aside className={`${s.operator} ${open ? s.open : ""}`} aria-label="宮古島オペレーター">
      {open && (
        <div className={s.panel}>
          <header>
            <div className={`${s.core} ${listening || speaking ? s.activeCore : ""}`}><span /></div>
            <div><strong>MIYAKO OPERATOR</strong><small>{listening ? "音声入力中" : speaking ? "回答を読み上げ中" : "質問できます"}</small></div>
            <button type="button" onClick={closePanel} aria-label="閉じる">×</button>
          </header>
          {speaking && (
            <button className={s.stopVoiceButton} type="button" onClick={stopSpeaking}>
              <span aria-hidden="true">■</span>
              音声を停止
            </button>
          )}
          {listening && (
            <div className={s.listeningStatus} role="status" aria-live="assertive">
              <span aria-hidden="true" />
              <div>
                <strong>音声入力 ON</strong>
                <small>マイクに向かって話してください</small>
              </div>
            </div>
          )}
          <div className={s.messages} ref={messageListRef} aria-live="polite">
            {messages.map((message, index) => <p key={`${message.role}-${index}`} className={message.role === "user" ? s.userMessage : s.operatorMessage}>{message.text}</p>)}
          </div>
          <div className={s.suggestions}>{suggestions.map((label) => <button key={label} type="button" onClick={() => ask(label)}>{label}</button>)}</div>
          <form onSubmit={(event) => { event.preventDefault(); ask(input); }}>
            <button
              type="button"
              className={`${s.micButton} ${listening ? `${s.controlActive} ${s.listeningMic}` : ""}`}
              onClick={toggleListening}
              aria-label={listening ? "音声入力を停止" : "音声入力を開始"}
              title={listening ? "音声入力を停止" : "音声入力を開始"}
            >
              <span aria-hidden="true">{listening ? "■" : "🎙"}</span>
              <small>{listening ? "停止" : "話す"}</small>
            </button>
            <input value={input} onChange={(event) => setInput(event.target.value)} placeholder="宮古島について聞いてみる" aria-label="質問" />
            <button
              type="button"
              className={`${s.voiceToggle} ${voiceEnabled ? s.controlActive : ""}`}
              onClick={() => {
                setVoiceEnabled((value) => !value);
                stopSpeaking();
              }}
              aria-label={voiceEnabled ? "回答の読み上げを停止" : "回答を読み上げる"}
              title={voiceEnabled ? "回答の読み上げを停止" : "回答を読み上げる"}
            >
              <span aria-hidden="true">{voiceEnabled ? "🔊" : "🔇"}</span>
              {voiceEnabled ? "回答を読み上げる" : "読み上げなし"}
            </button>
            <button type="submit" aria-label="送信">➤</button>
          </form>
          <button className={s.mapShortcut} type="button" onClick={() => { setScreen?.("map"); closePanel(); }}>おすすめスポットをマップで見る</button>
          <small className={s.disclaimer}>安全情報は参考値です。現地の遊泳可否・警報を優先してください。</small>
        </div>
      )}
      <button className={s.launcher} type="button" onClick={() => { if (open) closePanel(); else setOpen(true); }} aria-expanded={open}>
        <span className={`${s.launcherCore} ${listening || speaking ? s.activeCore : ""}`} />
        <span><strong>宮古島音声案内</strong><small>音声で聞く</small></span>
      </button>
    </aside>
  );
};

export default OceanOperator;
